import { useState, useEffect, useRef } from 'react'
import { Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import type { RefundApprovalFormData, RefundApprovalDecision } from '../types'

interface RefundApprovalFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: RefundApprovalFormData) => Promise<void> | void
  onSuccess?: () => void
  /** Original refund amount in cents */
  refundAmount?: number
  /** Currency code e.g. "USD" */
  currency?: string
}

// ── Currency helpers ──────────────────────────────────────────────────────────

function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', JPY: '¥' }
  return symbols[currency] ?? currency
}

function formatCents(cents: number, currency = 'USD'): string {
  return `${getCurrencySymbol(currency)}${(cents / 100).toFixed(2)}`
}

// ── Reason options filtered by decision ──────────────────────────────────────

const APPROVE_REASONS = [
  { value: 'valid_request',        label: 'Valid request' },
  { value: 'policy_compliant',     label: 'Policy compliant' },
  { value: 'customer_satisfaction', label: 'Customer satisfaction' },
  { value: 'merchant_error',       label: 'Merchant error' },
  { value: 'other',                label: 'Other' },
]

const DECLINE_REASONS = [
  { value: 'invalid_request',   label: 'Invalid request' },
  { value: 'policy_violation',  label: 'Policy violation' },
  { value: 'fraudulent',        label: 'Suspected fraud' },
  { value: 'other',             label: 'Other' },
]

const AUTO_CLOSE_MS = 1500

const DECISION_OPTIONS: Array<{ value: RefundApprovalDecision; label: string }> = [
  { value: 'approve', label: 'Approve' },
  { value: 'modify',  label: 'Modify amount' },
  { value: 'decline', label: 'Decline' },
]

export function RefundApprovalForm({
  open,
  onClose,
  onSubmit,
  onSuccess,
  refundAmount,
  currency = 'USD',
}: RefundApprovalFormProps) {
  const [decision, setDecision]           = useState<RefundApprovalDecision | ''>('')
  const [modifiedInput, setModifiedInput] = useState('0.00')
  const [modifiedCents, setModifiedCents] = useState(0)
  const [amountError, setAmountError]     = useState<string | null>(null)
  const [reason, setReason]               = useState('')
  const [notes, setNotes]                 = useState('')
  const [isLoading, setIsLoading]         = useState(false)
  const [status, setStatus]               = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setDecision('')
      setModifiedInput('0.00')
      setModifiedCents(0)
      setAmountError(null)
      setReason('')
      setNotes('')
      setIsLoading(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open])

  // Clear reason when decision changes
  useEffect(() => { setReason('') }, [decision])

  // ── Amount handling ───────────────────────────────────────────────────────

  const handleAmountChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '')
    setModifiedInput(cleaned)
    setAmountError(null)
    const num = parseFloat(cleaned)
    if (!cleaned || isNaN(num)) { setModifiedCents(0); return }
    const cents = Math.round(num * 100)
    if (refundAmount !== undefined && cents > refundAmount) {
      setAmountError(`Cannot exceed ${formatCents(refundAmount, currency)}`)
      return
    }
    setModifiedCents(cents)
  }

  const handleAmountBlur = () => {
    const num = parseFloat(modifiedInput)
    if (!modifiedInput || isNaN(num)) {
      setModifiedInput('0.00')
      setModifiedCents(0)
    } else {
      setModifiedInput(num.toFixed(2))
    }
  }

  // ── Validation ───────────────────────────────────────────────────────────

  const isValid =
    (decision as string) !== '' &&
    reason !== '' &&
    (decision !== 'modify' || (modifiedCents > 0 && !amountError))

  const currentReasons = decision === 'decline' ? DECLINE_REASONS : APPROVE_REASONS
  const currencySymbol = getCurrencySymbol(currency)

  // ── Submit ────────────────────────────────────────────────────────────────

  const successMessage =
    decision === 'approve' ? 'Refund approved' :
    decision === 'modify'  ? 'Modified refund approved' :
    'Refund declined'

  const handleSubmit = async () => {
    if (!isValid || isLoading || decision === '') return
    setIsLoading(true)
    try {
      await onSubmit({
        decision,
        modifiedAmount: decision === 'modify' ? modifiedCents : undefined,
        reason,
        notes: notes.trim(),
      })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onSuccess?.()
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-[480px] gap-0 p-0 overflow-hidden">
        <DialogResultTransition
          status={status}
          successMessage={successMessage}
          errorMessage="Failed to process refund"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-[17px] font-semibold leading-tight">
            Review refund request
          </DialogTitle>
        </DialogHeader>

        {/* ── Scrollable body ── */}
        <div className="px-6 pt-4 pb-6 space-y-5 overflow-y-auto max-h-[calc(100vh-160px)]">

          {/* Info banner with original amount */}
          <div className="flex gap-3 rounded-xl bg-[#EFF6FF] p-3.5">
            <Info className="h-4 w-4 text-[#3B82F6] mt-0.5 shrink-0" />
            <p className="text-[13px] leading-[1.45] text-[#1E40AF]">
              {refundAmount !== undefined
                ? `A refund of ${formatCents(refundAmount, currency)} has been requested. Review and approve, modify, or decline.`
                : 'A refund has been requested. Review and approve, modify the amount, or decline.'}
            </p>
          </div>

          {/* Decision — pill toggles */}
          <div className="space-y-2">
            <label className="text-[13px] font-medium text-foreground">
              Decision
            </label>
            <div className="flex flex-wrap gap-2">
              {DECISION_OPTIONS.map(({ value, label }) => {
                const isSelected = decision === value
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setDecision(value)}
                    className={cn(
                      'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-medium border transition-colors',
                      isSelected
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-card text-foreground border-input hover:border-foreground/40',
                    )}
                    aria-pressed={isSelected}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Conditional: modified amount input */}
          {decision === 'modify' && (
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-foreground">
                Modified refund amount
              </label>
              <div
                className={cn(
                  'flex items-center gap-2 rounded-xl border bg-card px-4 h-[52px] transition-colors focus-within:border-foreground',
                  amountError ? 'border-destructive' : 'border-input',
                )}
              >
                <span className="text-[22px] font-medium text-muted-foreground select-none">
                  {currencySymbol}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={modifiedInput}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  onBlur={handleAmountBlur}
                  onFocus={(e) => { if (e.target.value === '0.00') setModifiedInput('') }}
                  className="flex-1 bg-transparent text-[22px] font-medium outline-none placeholder:text-muted-foreground/50 min-w-0"
                  placeholder="0.00"
                  aria-label="Modified refund amount"
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  {amountError && (
                    <p className="text-[12px] text-destructive">{amountError}</p>
                  )}
                </div>
                {refundAmount !== undefined && (
                  <p className="text-[12px] text-muted-foreground text-right">
                    Requested{' '}
                    <span className="font-medium text-foreground">
                      {formatCents(refundAmount, currency)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Reason chips — shown once a decision is selected */}
          {decision !== '' && (
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-foreground">
                Reason
              </label>
              <div className="flex flex-wrap gap-2">
                {currentReasons.map((r) => {
                  const isSelected = reason === r.value
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setReason(r.value)}
                      className={cn(
                        'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-medium border transition-colors',
                        isSelected
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-card text-foreground border-input hover:border-foreground/40',
                      )}
                      aria-pressed={isSelected}
                    >
                      {r.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Notes (optional) */}
          <div className="space-y-2">
            <label className="text-[13px] font-medium text-foreground">
              Notes{' '}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional context..."
              rows={2}
              className="w-full rounded-xl border border-input bg-card px-3.5 py-3 text-[14px] resize-none outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
              aria-label="Additional notes"
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4 bg-card">
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-5"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="action"
            className="rounded-full px-5"
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
          >
            {isLoading
              ? 'Processing...'
              : decision === 'approve'
              ? 'Approve refund'
              : decision === 'modify'
              ? 'Approve modified amount'
              : decision === 'decline'
              ? 'Decline refund'
              : 'Submit'}
          </Button>
        </div>

        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
